import * as React from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import ExpandMoreIcon from '@assets/images/FilterDownArrow.svg';
import CustomButton from '@ui-components/Button/CustomButton';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import { useEffect, useState } from 'react';
import {
  type PartnerFacilitiesType,
  type IndeterminateCheckboxFacilityProps,
  type Facilities,
} from '../../API/services/caseManagerServices';
import '../components/component.css';

export default function FilterCheckbox({
  searchText,
  applyButtonCliked,
  facilityPartners,
  clearFilter,
  getFacilityPartnerCount,
  storedPreviousValues,
}: Readonly<{
  searchText: string;
  applyButtonCliked: (data: IndeterminateCheckboxFacilityProps[]) => void;
  facilityPartners: PartnerFacilitiesType[];
  clearFilter: boolean;
  getFacilityPartnerCount: (data: IndeterminateCheckboxFacilityProps[]) => void;
  storedPreviousValues: IndeterminateCheckboxFacilityProps[];
}>) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const [filterData, setFilterData] = useState<PartnerFacilitiesType[]>([]);
  useEffect(() => {
    if (facilityPartners) {
      setFilterData(structuredClone(facilityPartners));
    }
  }, [facilityPartners]);

  const [checkedIds, setCheckedIds] = useState<
    IndeterminateCheckboxFacilityProps[]
  >(structuredClone(storedPreviousValues));
  useEffect(() => {
    if (clearFilter) {
      setCheckedIds([]);
    }
  }, [clearFilter]);

  useEffect(() => {
    const dataForSearch = structuredClone(facilityPartners);
    const searchedObject = dataForSearch?.filter(
      (facility) =>
        facility?.facilities?.some((hospital) =>
          hospital.name.toLowerCase().includes(searchText.toLowerCase())
        ) ||
        facility.partnerName.toLowerCase().includes(searchText.toLowerCase())
    );

    setFilterData(searchedObject);
  }, [searchText, facilityPartners]);

  const handleChangeFacility = (
    e: React.ChangeEvent<HTMLInputElement>,
    faciltyId: number,
    faciltyName: string,
    partnerIds: Facilities[]
  ) => {
    const prevCheckedIds = [...checkedIds];
    let newCheckedIds: IndeterminateCheckboxFacilityProps[] = [];

    const existingFacility: IndeterminateCheckboxFacilityProps | undefined =
      prevCheckedIds?.find(
        (item: IndeterminateCheckboxFacilityProps) =>
          item.partnerId === faciltyId
      );

    if (existingFacility) {
      existingFacility.checked = e.target.checked;
      existingFacility.facilities.forEach((facility) => {
        facility.checked = e.target.checked;
      });
      existingFacility.checkFacility = e.target.checked;

      if (
        existingFacility.checkInderterminate === true &&
        e.target.checked === true
      ) {
        existingFacility.checkFacility = null;
        existingFacility.checkInderterminate = false;
        existingFacility.facilities.forEach((facility) => {
          facility.checked = !e.target.checked;
        });
      }

      newCheckedIds = [...prevCheckedIds];
    } else {
      const newFacility = {
        partnerName: faciltyName,
        partnerId: faciltyId,
        checked: e.target.checked,
        facilities: partnerIds.map((facilityItem) => ({
          id: facilityItem.id,
          name: facilityItem.name,
          checked: e.target.checked,
        })),
        checkFacility: e.target.checked,
        checkInderterminate: !e.target.checked,
      };

      newCheckedIds = prevCheckedIds.concat([newFacility]);
    }

    setCheckedIds(() => newCheckedIds);
    getFacilityPartnerCount(newCheckedIds);
  };
  const handleChangePartner = (
    e: React.ChangeEvent<HTMLInputElement>,
    facilityId: number,
    partnerId: number,
    facilityList: Facilities[],
    faciltyName: string
  ) => {
    const prevCheckedIds = [...checkedIds];
    let newCheckedIds: IndeterminateCheckboxFacilityProps[] = [];

    const existingFacility = prevCheckedIds?.find(
      (item) => item.partnerId === facilityId
    );
    if (existingFacility) {
      const facilityIndex = existingFacility.facilities.findIndex(
        (facility) => facility.id === partnerId
      );

      if (facilityIndex !== -1) {
        existingFacility.facilities[facilityIndex].checked = e.target.checked;
        const allFacilitiesChecked = existingFacility.facilities.every(
          (facility) => facility.checked
        );
        existingFacility.checked = allFacilitiesChecked;
      }

      const hasAnyFacilityUnchecked = existingFacility?.facilities?.some(
        (facility) => !facility.checked
      );

      if (hasAnyFacilityUnchecked) {
        existingFacility.checkFacility = false;
        existingFacility.checkInderterminate = true;
      }
      const allFacilitiesUnchecked = existingFacility?.facilities?.every(
        (facility) => !facility.checked
      );
      const allFacilitiesChecked = existingFacility?.facilities?.every(
        (facility) => facility.checked
      );

      if (allFacilitiesUnchecked) {
        existingFacility.checkInderterminate = false;
        existingFacility.checkFacility = null;
      } else if (allFacilitiesChecked) {
        existingFacility.checkInderterminate = false;
        existingFacility.checkFacility = true;
      } else {
        existingFacility.checkInderterminate = true;
        existingFacility.checkFacility = null;
      }
      newCheckedIds = [...prevCheckedIds];
    } else {
      const newFacility = {
        partnerName: faciltyName,
        partnerId: facilityId,
        checked: e.target.checked,
        facilities: facilityList.map((FacilityItem) => ({
          id: FacilityItem.id,
          name: FacilityItem.name,
          checked: false,
        })),
        checkInderterminate: true,
        checkFacility: false,
      };

      newFacility.facilities.forEach((facility) => {
        if (facility.id === partnerId) {
          facility.checked = e.target.checked;
          if (newFacility.facilities.length === 1) {
            newFacility.checkFacility = e.target.checked;
            newFacility.checkInderterminate = !e.target.checked;
          }
        }
      });
      newCheckedIds = prevCheckedIds.concat([newFacility]);
    }
    setCheckedIds(() => newCheckedIds);
    getFacilityPartnerCount(newCheckedIds);
  };

  const children = (
    facilities: Facilities[],
    faciltyId: number,
    faciltyName: string
  ) => {
    return facilities.map((facilityVal: Facilities) => (
      <Box
        sx={{ display: 'flex', flexDirection: 'column', ml: 3 }}
        key={facilityVal.id}
      >
        <FormControlLabel
          label={facilityVal.name}
          control={
            <Checkbox
              checked={
                checkedIds
                  ?.find((item) => item.partnerId === faciltyId)
                  ?.facilities?.find(
                    (facility) => facility.id === facilityVal.id
                  )?.checked === true
              }
              onChange={(e) =>
                handleChangePartner(
                  e,
                  faciltyId,
                  facilityVal.id,
                  facilities,
                  faciltyName
                )
              }
            />
          }
        />
      </Box>
    ));
  };
  const handleExpand = (id: number) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };
  const handleApplyButtonClick = () => {
    applyButtonCliked(checkedIds);
  };

  return (
    <Box className="ml-20 mr-20">
      {(!filterData || filterData.length === 0) && (
        <Box className="text-align-center">
          <CustomTypography
            variant="body2"
            color="textDisabled"
            id="noAcceptedConsults"
          >
            No records found
          </CustomTypography>
        </Box>
      )}
      {filterData?.length > 0 &&
        filterData?.map((facilityVal) => (
          <Box key={facilityVal.partnerId}>
            <Box className="flex-justify-space-between" gap={6}>
              <FormControlLabel
                label={facilityVal.partnerName}
                control={
                  <Checkbox
                    checked={Boolean(
                      checkedIds.find(
                        (item) => item.partnerId === facilityVal.partnerId
                      )?.checkFacility
                    )}
                    indeterminate={
                      checkedIds.find(
                        (item) => item.partnerId === facilityVal.partnerId
                      )?.checkInderterminate ?? false
                    }
                    onChange={(e) =>
                      handleChangeFacility(
                        e,
                        facilityVal.partnerId,
                        facilityVal.partnerName,
                        facilityVal.facilities
                      )
                    }
                  />
                }
              />

              <Box
                className="filter-expand-icon"
                onClick={() => handleExpand(facilityVal.partnerId)}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    transform: expanded[facilityVal.partnerId]
                      ? 'rotate(180deg)'
                      : 'rotate(0deg)',
                    transition: 'transform 0.3s ease',
                  }}
                >
                  <ExpandMoreIcon />
                </Box>
              </Box>
            </Box>

            {expanded?.[facilityVal.partnerId] &&
              children(
                facilityVal.facilities,
                facilityVal.partnerId,
                facilityVal.partnerName
              )}
          </Box>
        ))}
      {filterData?.length !== 0 && (
        <Box className="mt-20">
          <CustomButton
            id="btnFilterApply"
            variant="contained"
            color="primary"
            className="button-width"
            onClick={handleApplyButtonClick}
          >
            Apply
          </CustomButton>
        </Box>
      )}
    </Box>
  );
}
